import React, { useState, useEffect } from 'react'
import CMSData from 'aldoo-ra/CMS/cms-data'
import AldooLayoutRenderer from './AldooLayoutRenderer'
import VBECSRenderer from './VBECSRenderer'
import Value from 'aldoo-ra/Value'
import { PermissionAPI } from 'aldoo-ra/Permissions/api'
import { navigate } from 'vike/client/router'

/**
 * Renders page content with layouts/slots
 */
const renderPageContent = (page, initialViewportWidth, ecsNodes, ecsPrefabs, ssrThemeData) => {
  const pageContent = page.content || page
  const [auth] = Value("user-credentials")
  const [isAdmin, setIsAdmin] = useState(null)

  // Check publish status
  const status = pageContent.status?.value || pageContent.status

  // Check admin permission for draft/scheduled pages
  useEffect(() => {
    const checkPermission = async () => {
      if (status === 'draft' || status === 'scheduled') {
        if (auth?._id) {
          try {
            const hasPermission = await PermissionAPI.hasPermission(auth._id, 'role:admin')
            setIsAdmin(hasPermission)

            // If not admin, navigate to 404
            if (!hasPermission) {
              navigate('/404', { overwriteLastHistoryEntry: true })
            }
          } catch (error) {
            console.error('Failed to check admin permission:', error)
            navigate('/404', { overwriteLastHistoryEntry: true })
          }
        } else {
          // No user logged in, navigate to 404
          navigate('/404', { overwriteLastHistoryEntry: true })
        }
      } else {
        setIsAdmin(true) // Published pages don't need admin permission
      }
    }

    checkPermission()
  }, [auth, status])

  // Show loading while checking permissions for draft/scheduled pages
  if ((status === 'draft' || status === 'scheduled') && isAdmin === null) {
    return null
  }

  if (status === 'draft' || status === 'scheduled') {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`AldooPageRenderer: Page "${pageContent.name}" status is "${status}"`)
    }
  }

  // Use nodes from pageContext (optimized) or fallback to page object
  const nodes = ecsNodes || pageContent.ecs_nodes;
  const prefabs = ecsPrefabs || pageContent.ecs_prefabs || [];

  // Check for VBECS content first
  if (nodes && Array.isArray(nodes) && nodes.length > 0) {
    return (
      <div className="aldoo-page vbecs-page w-full h-full min-h-screen">
        <VBECSRenderer
          nodes={nodes}
          prefabs={prefabs}
          initialViewportWidth={initialViewportWidth}
          ssrThemeData={ssrThemeData}
        />
      </div>
    )
  }

  // Get layout references - these can be either layouts or slots
  const layoutRefs = pageContent.layout || []

  if (layoutRefs.length === 0) {
    console.warn(`AldooPageRenderer: Page "${pageContent.name}" has no layout/slots assigned`)
    return (
      <div className="aldoo-page-no-layout p-4">
        <p className="text-admin_text_secondary">
          Page has no layout configured
        </p>
      </div>
    )
  }

  // Detect if layout refs are actually slot refs (new structure)
  // Check for slot characteristics: has 'id', 'component', 'position', 'size' fields
  const firstRef = layoutRefs[0]
  const firstContent = firstRef?.content || firstRef
  const isSlotBasedLayout = firstContent?.type === 'AldooLayoutSlot' ||
                           (firstContent?.id && firstContent?.component && firstContent?.position && firstContent?.size)

  if (isSlotBasedLayout) {
    // New structure: Page → Slots (directly)
    const virtualLayout = {
      id: 'page-slots',
      name: 'Page Slots',
      slots: layoutRefs,
    }

    return (
      <div
        className="aldoo-page"
        data-page-id={pageContent.id || pageContent.name}
        data-page-name={pageContent.name}
      >
        <AldooLayoutRenderer
          key="page-slots"
          layout={virtualLayout}
          pageData={{
            id: pageContent.id,
            name: pageContent.name,
            description: pageContent.description,
            link: pageContent.link,
            status: pageContent.status,
            _id: page._id,
          }}
        />
      </div>
    )
  }

  // Old structure: Page → Layouts → Slots
  return (
    <div
      className="aldoo-page"
      data-page-id={pageContent.id || pageContent.name}
      data-page-name={pageContent.name}
    >
      {layoutRefs.map((layoutRef, index) => {
        const layout = layoutRef.content || layoutRef

        if (!layout || !layout.id) {
          console.warn(`AldooPageRenderer: Invalid layout at index ${index} in page "${pageContent.name}"`, layoutRef)
          return null
        }

        return (
          <AldooLayoutRenderer
            key={layout._id || layout.id || index}
            layout={layout}
            pageData={{
              id: pageContent.id,
              name: pageContent.name,
              description: pageContent.description,
              link: pageContent.link,
              status: pageContent.status,
              _id: page._id,
            }}
          />
        )
      })}
    </div>
  )
}

/**
 * AldooPageRenderer
 * Dynamically renders a page from CMS with its layouts and components
 *
 * @param {Object} pageData - Pre-fetched page data (optional, for SSR)
 * @param {string} pageId - Page ID to fetch if pageData not provided
 * @param {string} pageName - Page name to fetch if pageData not provided
 * @param {Object} pageContext - Context from Vike (contains SSR data)
 */
const AldooPageRenderer = ({ pageData: initialPageData, pageId, pageName, pageContext }) => {
  // If we have initial data, render it directly without fetching
  if (initialPageData) {
    return renderPageContent(
        initialPageData,
        pageContext?.initialViewportWidth,
        pageContext?.ecs_nodes,
        pageContext?.ecs_prefabs,
        pageContext?.themeData
    )
  }

  // Otherwise, fetch from CMS
  const match = React.useMemo(() => {
    if (pageId) return { '_id': pageId }
    if (pageName) return { 'content.name': pageName }
    return null
  }, [pageId, pageName])

  const { data, loading, error } = CMSData({
    contentType: 'AldooPage',
    match,
    manual: false,
  })

  if (loading) {
    return (
      <div className="aldoo-page-loading" role="status" aria-busy="true">
        {/* Loading indicator - can be customized */}
      </div>
    )
  }

  if (error) {
    console.error('AldooPageRenderer: Error loading page', error)
    return (
      <div className="aldoo-page-error p-4">
        <p className="text-admin_error">Error loading page: {error}</p>
      </div>
    )
  }

  const page = data && data.length > 0 ? data[0] : null

  if (!page) {
    console.warn('AldooPageRenderer: No page data available', { pageId, pageName })
    return (
      <div className="aldoo-page-not-found p-4">
        <p className="text-admin_text_secondary">Page not found</p>
      </div>
    )
  }

  return renderPageContent(
      page,
      pageContext?.initialViewportWidth,
      pageContext?.ecs_nodes,
      pageContext?.ecs_prefabs,
      pageContext?.themeData
  )
}

// onBeforeRender hook for AldooPage SEO meta tags
export const onBeforeRender = async (pageContext) => {
  const requestPath = pageContext.urlParsed?.pathname || pageContext.urlPathname || '/'

  try {
    const { getPageByUrl } = await import('../registry/page-routes.js')
    const { url } = await import('aldoo-ra/CMS/url')

    // Check if this URL matches a dynamic page
    const dynamicPage = await getPageByUrl(requestPath)

    if (dynamicPage) {
      const pageContent = dynamicPage.content || dynamicPage

      // Use SEO fields if available, fallback to regular fields
      const seoTitle = pageContent.seo_title || pageContent.name || 'Page'
      const seoDescription = pageContent.seo_description || pageContent.description || ''
      const seoKeywords = pageContent.seo_keywords || ''

      // Handle image field which can be a string or object with url property
      const seoImageRaw = pageContent.seo_og_image
      const seoImage = (typeof seoImageRaw === 'object' && seoImageRaw?.url)
        ? seoImageRaw.url
        : seoImageRaw || '/static/images/og_image.jpg'

      // Build canonical URL
      const baseUrl = typeof window !== 'undefined'
        ? `${window.location.protocol}//${window.location.host}`
        : 'https://aqua-mail.com' // Fallback for SSR

      const canonicalUrl = `${baseUrl}${requestPath}`

      return {
        title: seoTitle,
        description: seoDescription,
        thumbnail: url(seoImage) || seoImage,
        tags: seoKeywords,
        metadata: {
          ogType: 'website',
          canonicalUrl: canonicalUrl
        }
      }
    }
  } catch (error) {
    console.error('AldooPageRenderer onBeforeRender ERROR:', error)
  }

  return {}
}

export default AldooPageRenderer
